import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Package, Edit2, Save, X, Plus, Clipboard, DollarSign } from 'lucide-react';

const fmt = n => Number(n || 0).toLocaleString();

const STATUS_STAGE = {
  pending:     { label: 'New Sale',       color: 'bg-amber-100 text-amber-700' },
  color_match: { label: 'Color Match',    color: 'bg-purple-100 text-purple-700' },
  confirmed:   { label: 'Confirmed',      color: 'bg-blue-100 text-blue-700' },
  scheduled:   { label: 'Scheduled',      color: 'bg-indigo-100 text-indigo-700' },
  in_progress: { label: 'Day of Install', color: 'bg-orange-100 text-orange-700' },
};

// ── Edit modal ────────────────────────────────────────────────────────────────
// Two modes:
//   "set"  → weekly count. Sets the TOTAL number owned (physical + committed to active jobs).
//            Internally stored as channels_in_stock = total - activeJobsChannels. No pay logged.
//   "add"  → restock. Adds delta on top of current total. Logs transaction (pays $rate per channel).
function EditStockModal({ item, type, onSave, onClose, payRate = 1.5 }) {
  const isChannel = type === 'channel';

  // Channels committed to confirmed/scheduled/in_progress jobs (already deducted from channels_in_stock)
  const fpc = isChannel ? (item.feet_per_channel || 6) : 1;
  const activeJobsFt = isChannel
    ? ['confirmed', 'scheduled', 'in_progress'].reduce(
        (s, st) => s + (item.by_status?.[st]?.footage || 0), 0)
    : 0;
  const activeJobsChannels = isChannel ? Math.ceil(activeJobsFt / fpc) : 0;

  // TOTAL owned = free physical + committed-to-jobs
  const currentStored = isChannel ? item.channels_in_stock : item.qty_in_stock;
  const currentTotal  = isChannel ? currentStored + activeJobsChannels : currentStored;

  const [mode, setMode] = useState('set');   // 'set' | 'add'
  const [setValue, setSetValue] = useState(currentTotal);
  const [addValue, setAddValue] = useState('');
  const [saving, setSaving] = useState(false);

  const addNum = Number(addValue || 0);
  const resultingTotal = mode === 'set' ? Number(setValue || 0) : currentTotal + addNum;
  const payEarned = isChannel && mode === 'add' && addNum > 0 ? addNum * payRate : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'set') {
        if (isChannel) {
          // Convert total back to free physical count
          const newFreePhysical = Number(setValue) - activeJobsChannels;
          await api.put(`/inventory/channels/${item.id}`, {
            channels_in_stock: newFreePhysical,
            channels_needed: item.channels_needed,
            notes: item.notes,
          });
        } else {
          await api.put(`/inventory/general/${item.id}`, {
            qty_in_stock: Number(setValue),
            qty_needed: item.qty_needed,
            buffer_qty: item.buffer_qty,
            notes: item.notes,
          });
        }
      } else {
        // add mode: use /restock so transaction is logged + pay credited
        if (!addNum || addNum <= 0) { setSaving(false); return; }
        await api.post('/inventory/restock', {
          items: [{ id: item.id, type: isChannel ? 'channel' : 'general', quantity: addNum }]
        });
      }
      onSave();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md my-auto max-h-[95vh] overflow-y-auto overscroll-contain flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 sticky top-0 bg-white border-b border-gray-100 z-10">
          <h3 className="font-semibold">{isChannel ? item.color_name : item.item_name}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="px-5 pt-4 pb-5">

        {/* Current state summary */}
        <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-gray-500">Current total owned</span>
            <span className="font-bold text-gray-800 text-lg">
              {currentTotal.toLocaleString()} {isChannel ? 'channels' : 'units'}
            </span>
          </div>
          {isChannel && (
            <p className="text-xs text-gray-400 text-right">
              = {(currentTotal * fpc).toLocaleString()} ft
            </p>
          )}
          {isChannel && activeJobsChannels > 0 && (
            <div className="pt-1.5 mt-1.5 border-t border-gray-200 text-xs space-y-0.5">
              <div className="flex justify-between text-gray-500">
                <span>On warehouse shelf</span>
                <span>{currentStored.toLocaleString()} ch ({(currentStored * fpc).toLocaleString()} ft)</span>
              </div>
              <div className="flex justify-between text-blue-600">
                <span>Committed to active jobs</span>
                <span>{activeJobsChannels.toLocaleString()} ch ({activeJobsFt.toLocaleString()} ft)</span>
              </div>
            </div>
          )}
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-xl border overflow-hidden mb-4">
          <button type="button" onClick={() => setMode('set')}
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
              mode === 'set' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}>
            <Clipboard className="w-4 h-4" /> Weekly Count
          </button>
          <button type="button" onClick={() => setMode('add')}
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
              mode === 'add' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}>
            <Plus className="w-4 h-4" /> Add New Stock
          </button>
        </div>

        {mode === 'set' ? (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              What's the total count you own right now?
            </label>
            <input
              type="number" min="0" value={setValue}
              onChange={e => setSetValue(e.target.value)}
              className="w-full px-3 py-2.5 border-2 border-nova-200 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">
              This <span className="font-semibold">replaces</span> the total — include both warehouse shelf and channels already committed to confirmed jobs.
            </p>
            {isChannel && setValue !== '' && (
              <p className="text-xs text-gray-500 mt-1">
                = {(Number(setValue) * fpc).toLocaleString()} ft total
                {activeJobsChannels > 0 && (
                  <> · {(Math.max(0, Number(setValue) - activeJobsChannels)).toLocaleString()} ch free to sell after jobs</>
                )}
              </p>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              How many new {isChannel ? 'channels' : 'units'} did you add?
            </label>
            <input
              type="number" min="1" value={addValue}
              onChange={e => setAddValue(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2.5 border-2 border-green-200 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">
              This <span className="font-semibold text-green-600">adds on top</span> of the current total.
            </p>
            {addNum > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 mt-2 text-xs">
                <div className="flex justify-between text-green-800">
                  <span>New total will be:</span>
                  <span className="font-semibold">{resultingTotal.toLocaleString()} {isChannel ? 'channels' : 'units'}</span>
                </div>
                {isChannel && payEarned > 0 && (
                  <div className="flex justify-between text-green-700 mt-1 pt-1.5 border-t border-green-200">
                    <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />Pay earned</span>
                    <span className="font-semibold">${payEarned.toFixed(2)} ({addNum} × ${payRate.toFixed(2)})</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving || (mode === 'add' && addNum <= 0)}
            className={`flex-1 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1 ${
              mode === 'set' ? 'bg-nova-600 hover:bg-nova-700' : 'bg-green-600 hover:bg-green-700'
            }`}>
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Saving…' : mode === 'set' ? 'Save Count' : 'Add Stock'}
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-white border text-gray-600 rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

// ── Channel colors table ──────────────────────────────────────────────────────
function ChannelTable({ colors, isAdmin, onEdit }) {
  const [expanded, setExpanded] = useState(null);

  const toggle = id => setExpanded(prev => prev === id ? null : id);

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
            <th className="text-left px-4 py-3 font-medium">Color</th>
            <th className="text-right px-4 py-3 font-medium">
              Total Stock
              <div className="normal-case font-normal text-gray-400">owned</div>
            </th>
            <th className="text-right px-4 py-3 font-medium">
              In Active Jobs
              <div className="normal-case font-normal text-gray-400">confirmed → install</div>
            </th>
            <th className="text-right px-4 py-3 font-medium">
              Pending Hold
              <div className="normal-case font-normal text-gray-400">not yet confirmed</div>
            </th>
            <th className="text-right px-4 py-3 font-medium">
              Free to Sell
              <div className="normal-case font-normal text-gray-400">total − jobs − pending</div>
            </th>
            <th className="text-center px-4 py-3 font-medium">Status</th>
            {isAdmin && <th className="px-4 py-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {colors.map(color => {
            const fpc = color.feet_per_channel || 6;
            const byStatus = color.by_status || {};

            // Active jobs = confirmed/scheduled/in_progress (deducted from physical stock)
            const activeJobsFt = ['confirmed', 'scheduled', 'in_progress']
              .reduce((s, st) => s + (byStatus[st]?.footage || 0), 0);
            const activeJobsCount = ['confirmed', 'scheduled', 'in_progress']
              .reduce((s, st) => s + (byStatus[st]?.jobs?.length || 0), 0);

            // Pending hold = unconfirmed + color_match (soft reserve, NOT yet deducted)
            const pendingFt = (byStatus['pending']?.footage || 0) + (byStatus['color_match']?.footage || 0);
            const pendingCount = (byStatus['pending']?.jobs?.length || 0) + (byStatus['color_match']?.jobs?.length || 0);

            // Total stock = physical (after deductions) + active jobs (reconstruct original)
            const physicalFt = color.channels_in_stock * fpc;
            const totalFt = physicalFt + activeJobsFt;
            const totalCh = totalFt > 0 ? Math.ceil(totalFt / fpc) : 0;

            // Free = total − active jobs − pending hold
            const freeFt = totalFt - activeJobsFt - pendingFt;
            const freeCh = freeFt > 0 ? Math.floor(freeFt / fpc) : 0;
            const isOversold = freeFt < 0;

            const allActiveStages = Object.entries(byStatus).filter(([, v]) => v.jobs?.length > 0);
            const totalJobs = allActiveStages.reduce((s, [, v]) => s + v.jobs.length, 0);
            const isOpen = expanded === color.id;
            const shortfall = isOversold ? Math.ceil(Math.abs(freeFt) / fpc) : 0;

            return [
              <tr
                key={color.id}
                className={`hover:bg-gray-50 transition-colors ${isOversold ? 'bg-red-50' : ''} ${totalJobs > 0 ? 'cursor-pointer' : ''}`}
                onClick={() => totalJobs > 0 && toggle(color.id)}
              >
                <td className="px-4 py-3 font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {totalJobs > 0 && (
                      isOpen
                        ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        : <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    )}
                    {color.color_name}
                  </div>
                </td>

                {/* Total Stock */}
                <td className="px-4 py-3 text-right text-gray-700">
                  <span className="font-medium">{fmt(totalFt)} ft</span>
                  <span className="text-gray-400 text-xs ml-1">/ {fmt(totalCh)} ch</span>
                </td>

                {/* In Active Jobs */}
                <td className="px-4 py-3 text-right">
                  {activeJobsFt > 0 ? (
                    <span className="text-blue-700 font-medium">
                      {fmt(activeJobsFt)} ft
                      <span className="text-gray-400 text-xs ml-1">({activeJobsCount} job{activeJobsCount !== 1 ? 's' : ''})</span>
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>

                {/* Pending Hold */}
                <td className="px-4 py-3 text-right">
                  {pendingFt > 0 ? (
                    <span className="text-amber-700 font-medium">
                      {fmt(pendingFt)} ft
                      <span className="text-gray-400 text-xs ml-1">({pendingCount} sale{pendingCount !== 1 ? 's' : ''})</span>
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>

                {/* Free to Sell */}
                <td className="px-4 py-3 text-right">
                  {isOversold ? (
                    <span className="text-red-600 font-bold">Oversold {fmt(Math.abs(freeFt))} ft</span>
                  ) : (
                    <span className={`font-medium ${freeFt === 0 ? 'text-gray-400' : 'text-green-600'}`}>
                      {fmt(freeFt)} ft
                      <span className="text-gray-400 text-xs ml-1">/ {fmt(freeCh)} ch</span>
                    </span>
                  )}
                </td>

                {/* Status */}
                <td className="px-4 py-3 text-center">
                  {isOversold ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                      <AlertTriangle className="w-3 h-3" />
                      Order {shortfall} ch
                    </span>
                  ) : totalFt === 0 ? (
                    <span className="inline-flex px-2.5 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">Out of stock</span>
                  ) : (
                    <span className="inline-flex px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">OK</span>
                  )}
                </td>

                {isAdmin && (
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={e => { e.stopPropagation(); onEdit(color); }}
                      className="text-gray-300 hover:text-nova-600 transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>,

              /* Expanded reservation breakdown */
              isOpen && (() => {
                // Separate pending (soft hold) from confirmed+ (already deducted from physical stock)
                const softStages = allActiveStages.filter(([s]) => s === 'pending' || s === 'color_match');
                const deductedStages = allActiveStages.filter(([s]) => s !== 'pending' && s !== 'color_match');

                const renderJobs = (stageList) => stageList.map(([status, val]) => {
                  const stage = STATUS_STAGE[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
                  return (
                    <div key={status}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stage.color}`}>
                          {stage.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          {fmt(val.footage)} ft · {val.channels} ch
                        </span>
                      </div>
                      <div className="grid gap-1">
                        {val.jobs.map(job => (
                          <div key={job.id} className="flex items-center justify-between text-xs bg-white border rounded-lg px-3 py-1.5">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/sales/${job.id}`}
                                onClick={e => e.stopPropagation()}
                                className="font-medium text-nova-600 hover:underline"
                              >
                                {job.customer_name}
                              </Link>
                              {job.city && <span className="text-gray-400">· {job.city}</span>}
                            </div>
                            <div className="flex items-center gap-3 text-gray-500">
                              <span>{fmt(job.total_footage)} ft</span>
                              {job.scheduled_date && (
                                <span className="text-indigo-500 font-medium">
                                  {new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });

                return (
                  <tr key={`${color.id}-detail`} className="bg-gray-50/80">
                    <td colSpan={isAdmin ? 7 : 6} className="px-6 py-4">
                      <div className="space-y-4">
                        {softStages.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide">
                              Pending holds — not yet confirmed (reduces free to sell)
                            </p>
                            <div className="space-y-3">{renderJobs(softStages)}</div>
                          </div>
                        )}
                        {deductedStages.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">
                              Active jobs — already deducted from physical stock
                            </p>
                            <div className="space-y-3">{renderJobs(deductedStages)}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })(),
            ];
          })}

          {colors.length === 0 && (
            <tr>
              <td colSpan={isAdmin ? 7 : 6} className="text-center py-10 text-gray-400">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No channel colors set up
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── General inventory table ───────────────────────────────────────────────────
function GeneralTable({ items, isAdmin, onEdit }) {
  const [expanded, setExpanded] = useState(null);
  const toggle = id => setExpanded(prev => prev === id ? null : id);

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
            <th className="text-left px-4 py-3 font-medium">Item</th>
            <th className="text-right px-4 py-3 font-medium">In Stock</th>
            <th className="text-right px-4 py-3 font-medium">Reserved</th>
            <th className="text-right px-4 py-3 font-medium">Available</th>
            <th className="text-center px-4 py-3 font-medium">Status</th>
            {isAdmin && <th className="px-4 py-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map(item => {
            const isCritical = item.status === 'critical';
            const activeStages = Object.entries(item.by_status || {}).filter(([, v]) => v.qty > 0);
            const totalJobs = activeStages.reduce((s, [, v]) => s + v.jobs.length, 0);
            const isOpen = expanded === item.id;

            return [
              <tr
                key={item.id}
                className={`hover:bg-gray-50 transition-colors ${isCritical ? 'bg-red-50' : ''} ${totalJobs > 0 ? 'cursor-pointer' : ''}`}
                onClick={() => totalJobs > 0 && toggle(item.id)}
              >
                <td className="px-4 py-3 font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {totalJobs > 0 && (
                      isOpen
                        ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        : <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    )}
                    {item.item_name}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-medium text-gray-700">{fmt(item.qty_in_stock)}</td>
                <td className="px-4 py-3 text-right">
                  {item.total_reserved > 0 ? (
                    <span className="text-amber-700 font-medium">
                      {fmt(item.total_reserved)}
                      <span className="text-gray-400 text-xs ml-1">({totalJobs} job{totalJobs !== 1 ? 's' : ''})</span>
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-medium ${isCritical ? 'text-red-600' : item.effective_available === 0 ? 'text-gray-400' : 'text-green-600'}`}>
                    {fmt(item.effective_available)}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {isCritical ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                      <AlertTriangle className="w-3 h-3" /> Order now
                    </span>
                  ) : item.qty_in_stock === 0 ? (
                    <span className="inline-flex px-2.5 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">Out of stock</span>
                  ) : (
                    <span className="inline-flex px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">OK</span>
                  )}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={e => { e.stopPropagation(); onEdit(item); }}
                      className="text-gray-300 hover:text-nova-600 transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>,

              isOpen && (
                <tr key={`${item.id}-detail`} className="bg-amber-50/50">
                  <td colSpan={isAdmin ? 6 : 5} className="px-6 py-3">
                    <div className="space-y-3">
                      {activeStages.map(([status, val]) => {
                        const stage = STATUS_STAGE[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
                        return (
                          <div key={status}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stage.color}`}>
                                {stage.label}
                              </span>
                              <span className="text-xs text-gray-400">qty: {val.qty}</span>
                            </div>
                            <div className="grid gap-1">
                              {val.jobs.map(job => (
                                <div key={job.id} className="flex items-center justify-between text-xs bg-white border rounded-lg px-3 py-1.5">
                                  <div className="flex items-center gap-2">
                                    <Link
                                      to={`/sales/${job.id}`}
                                      onClick={e => e.stopPropagation()}
                                      className="font-medium text-nova-600 hover:underline"
                                    >
                                      {job.customer_name}
                                    </Link>
                                    {job.city && <span className="text-gray-400">· {job.city}</span>}
                                  </div>
                                  <div className="flex items-center gap-2 text-gray-500">
                                    <span>×{job.quantity_reserved}</span>
                                    {job.scheduled_date && (
                                      <span className="text-indigo-500 font-medium">
                                        {new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ),
            ];
          })}

          {items.length === 0 && (
            <tr>
              <td colSpan={isAdmin ? 6 : 5} className="text-center py-10 text-gray-400">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No general inventory items
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Inventory() {
  const { user } = useAuth();
  // "isAdmin" here really means "can edit inventory" — admin, manager, or whitelisted user (e.g. Jiwan)
  const isAdmin = user.role === 'admin' || user.role === 'manager' || ['jiwan', 'kushdeep'].includes((user.username || '').toLowerCase());
  const [tab, setTab] = useState('channels');
  const [general, setGeneral] = useState([]);
  const [channels, setChannels] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [payRate, setPayRate] = useState(1.5);

  const load = () => {
    api.get('/inventory/general').then(setGeneral);
    api.get('/inventory/channels').then(setChannels);
  };
  useEffect(() => {
    load();
    api.get('/inventory/pay-rate').then(r => setPayRate(r.ratePerChannel)).catch(() => {});
  }, []);

  const oversoldColors = channels.filter(c => c.is_oversold);
  const totalFreeFt = channels.reduce((s, c) => s + (c.free_feet || 0), 0);
  const totalReservedFt = channels.reduce((s, c) => s + (c.total_reserved_feet || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Inventory</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            <span className="text-green-600">{fmt(totalFreeFt)} ft free to sell</span>
            {totalReservedFt > 0 && (
              <span className="text-gray-400"> · {fmt(totalReservedFt)} ft reserved for active jobs</span>
            )}
          </p>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Order Needed alert */}
      {oversoldColors.length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
            <span className="font-semibold text-red-700 text-sm">
              {oversoldColors.length} color{oversoldColors.length !== 1 ? 's' : ''} need to be ordered
            </span>
          </div>
          <div className="divide-y divide-red-100">
            {oversoldColors.map(c => {
              const fpc = c.feet_per_channel || 6;
              const byStatus = c.by_status || {};
              // All jobs needing this color (pending + color_match + confirmed+)
              const allJobs = ['pending', 'color_match', 'confirmed', 'scheduled', 'in_progress']
                .flatMap(st => (byStatus[st]?.jobs || []).map(j => ({ ...j, stage: st })));
              // Shortfall: channels_in_stock already reflects confirmed+ deductions.
              // Only subtract soft-hold (pending/color_match) channels on top of that.
              const softFt = (byStatus['pending']?.footage || 0) + (byStatus['color_match']?.footage || 0);
              const softCh = softFt > 0 ? Math.ceil(softFt / fpc) : 0;
              const freeChannels = c.channels_in_stock - softCh;
              const shortfallCh = freeChannels < 0 ? Math.abs(freeChannels) : 0;
              const shortfallFt = shortfallCh * fpc;
              return (
                <div key={c.id} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-medium text-sm text-red-800">{c.color_name}</span>
                    <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                      Order {shortfallCh} ch ({shortfallFt} ft)
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {allJobs.map(j => (
                      <span key={j.id} className="text-xs bg-white border border-red-200 text-red-700 px-2 py-0.5 rounded">
                        {j.customer_name} · {j.total_footage} ft
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setTab('channels')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'channels' ? 'bg-nova-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}
        >
          Channel Colors
        </button>
        <button
          onClick={() => setTab('general')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'general' ? 'bg-nova-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}
        >
          General Equipment
        </button>
      </div>

      {/* Legend */}
      {tab === 'channels' && (
        <p className="text-xs text-gray-400 mb-3">
          Each channel = {channels[0]?.feet_per_channel || 6} ft ·{' '}
          <span className="text-blue-600">In Active Jobs</span> = confirmed through install (deducted from physical stock) ·{' '}
          <span className="text-amber-600">Pending Hold</span> = unconfirmed sales ·{' '}
          <span className="text-green-600">Free to Sell</span> = Total − Jobs − Pending · Click any row to see details
        </p>
      )}
      {tab === 'general' && (
        <p className="text-xs text-gray-400 mb-3">
          <span className="text-amber-600">Reserved</span> = units tied to active sales · Click any row to see which jobs
        </p>
      )}

      {/* Tables */}
      {tab === 'channels' && (
        <ChannelTable
          colors={channels}
          isAdmin={isAdmin}
          onEdit={item => setEditItem({ item, type: 'channel' })}
        />
      )}
      {tab === 'general' && (
        <GeneralTable
          items={general}
          isAdmin={isAdmin}
          onEdit={item => setEditItem({ item, type: 'general' })}
        />
      )}

      {editItem && (
        <EditStockModal
          item={editItem.item}
          type={editItem.type}
          payRate={payRate}
          onSave={() => { setEditItem(null); load(); }}
          onClose={() => setEditItem(null)}
        />
      )}
    </div>
  );
}
